import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../interceptors";

// Interview search over GET /job-posting/job-details/jobs/interviews (the same
// list the Interviews screen uses). Flattens jobs → interviews and lets the
// user search by candidate / position and filter by status. Row → report.

const PALETTE = [
  "#8B5CF6",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
];
const colorFor = (key = "") => {
  let h = 0;
  const s = String(key);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
};
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const statusClass = (status) => {
  const s = String(status || "").toUpperCase();
  if (s.includes("SCORE") || s === "COMPLETED" || s === "ACCEPTED")
    return "badge-success";
  if (s === "LIVE" || s.includes("PROGRESS")) return "badge-warning";
  if (s === "PENDING" || s === "AWAITING" || s === "CREATED")
    return "badge-neutral";
  if (s === "REJECTED" || s === "FAILED" || s === "EXPIRED")
    return "badge-danger";
  return "badge-neutral";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function SearchInterviews() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    document.title = "Search interviews - Ample Hire";
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axiosInstance.get(
          "/job-posting/job-details/jobs/interviews",
        );
        const data = r.data?.data ?? r.data;
        let flat = [];
        if (
          Array.isArray(data) &&
          data.length &&
          Array.isArray(data[0]?.interviews)
        ) {
          flat = data.flatMap((j) =>
            (j.interviews || []).map((iv) => ({
              ...iv,
              jobId: iv.jobId ?? j.jobId,
              position: iv.position || j.position,
            })),
          );
        } else if (Array.isArray(data?.interviews)) {
          flat = data.interviews;
        } else if (Array.isArray(data?.list)) {
          flat = data.list;
        } else if (Array.isArray(data)) {
          flat = data;
        }
        if (!cancelled) setRows(flat);
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.data?.message ||
              e.message ||
              "Failed to load interviews.",
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const statuses = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.status && set.add(String(r.status)));
    return ["ALL", ...Array.from(set)];
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "ALL" && String(r.status) !== statusFilter)
        return false;
      if (!query) return true;
      return (
        String(r.name || "")
          .toLowerCase()
          .includes(query) ||
        String(r.position || "")
          .toLowerCase()
          .includes(query) ||
        String(r.email || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [rows, q, statusFilter]);

  return (
    <div className="max-w-[1200px] mx-auto px-2 py-2 font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-vf-base font-heading">
          Search interviews
        </h1>
        <p className="text-sm text-vf-muted mt-1">
          Find interview sessions across all jobs by candidate, position, or
          status.
        </p>
      </div>

      {/* Filter bar */}
      <div className="card p-0 overflow-hidden mb-5">
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                className="input pl-9 w-64"
                placeholder="Search candidate or position…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`btn-sm text-xs ${statusFilter === s ? "btn-primary" : "btn-ghost"}`}
                >
                  {s === "ALL" ? "All" : s}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-vf-muted">
            {filtered.length} interview{filtered.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center">
            <div className="skeleton h-4 w-32 mx-auto rounded" />
          </div>
        ) : error ? (
          <div className="px-5 py-12 text-center text-sm text-vf-danger">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-vf-muted">
            No interviews match your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="tbl-header">
                  <th className="text-left px-4 py-3 w-[28%]">Candidate</th>
                  <th className="text-left px-4 py-3 w-[24%]">Position</th>
                  <th className="text-left px-4 py-3 w-[14%]">Status</th>
                  <th className="text-left px-4 py-3 w-[16%]">Invited</th>
                  <th className="px-4 py-3 w-[18%]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r.id ?? i}
                    className="tbl-row cursor-pointer"
                    onClick={() =>
                      r.id && navigate(`/admin/interview-report?id=${r.id}`)
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                          style={{ background: colorFor(r.candidateId ?? r.id) }}
                        >
                          {initials(r.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-vf-base truncate">
                            {r.name || "—"}
                          </div>
                          {r.email && (
                            <div className="text-[11px] text-vf-muted truncate">
                              {r.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-vf-muted truncate">
                      {r.position || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusClass(r.status)}>
                        {r.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-vf-muted">
                      {fmtDate(r.invitedAt || r.invited_at || r.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (r.id)
                            navigate(`/admin/interview-report?id=${r.id}`);
                        }}
                        className="btn-secondary btn-sm"
                        disabled={!r.id}
                      >
                        View report
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
